/**
 * 페이지/모달/폼 영역의 inline 가이드 박스
 *
 * 13개 파일에 흩어진 Info 박스 패턴을 통합. variant별 색상은 디자인 시스템과 정합:
 *   info    — blue   (기본 안내, "이 항목은 ~ 입니다")
 *   success — green  (완료 후 다음 단계 안내)
 *   warning — amber  (주의/제한 사항)
 *   tip     — purple (보너스 팁, 좋은 사례)
 */

'use client';

import type { ReactNode } from 'react';
import { Info, CheckCircle, AlertTriangle, Sparkles, X } from 'lucide-react';

export type AppHintBoxVariant = 'info' | 'success' | 'warning' | 'tip';

export interface AppHintBoxProps {
  /** 안내 본문 */
  children: ReactNode;
  /** 톤 — 기본 'info' */
  variant?: AppHintBoxVariant;
  /** 헤더 라인. 없으면 첫 줄이 곧 본문 */
  title?: ReactNode;
  /** 우측 상단 닫기 버튼 표시 */
  onDismiss?: () => void;
  /** 좌측 아이콘 비활성화 */
  hideIcon?: boolean;
  /** 추가 클래스 */
  className?: string;
}

const VARIANT_STYLES: Record<AppHintBoxVariant, { container: string; icon: string; title: string; iconNode: ReactNode }> = {
  info: {
    container: 'bg-blue-50 border-blue-200 text-blue-900',
    icon: 'text-blue-600',
    title: 'text-blue-900',
    iconNode: <Info className="w-4 h-4" aria-hidden="true" />,
  },
  success: {
    container: 'bg-green-50 border-green-200 text-green-900',
    icon: 'text-green-600',
    title: 'text-green-900',
    iconNode: <CheckCircle className="w-4 h-4" aria-hidden="true" />,
  },
  warning: {
    container: 'bg-amber-50 border-amber-200 text-amber-900',
    icon: 'text-amber-600',
    title: 'text-amber-900',
    iconNode: <AlertTriangle className="w-4 h-4" aria-hidden="true" />,
  },
  tip: {
    container: 'bg-purple-50 border-purple-200 text-purple-900',
    icon: 'text-purple-600',
    title: 'text-purple-900',
    iconNode: <Sparkles className="w-4 h-4" aria-hidden="true" />,
  },
};

export function AppHintBox({
  children,
  variant = 'info',
  title,
  onDismiss,
  hideIcon = false,
  className = '',
}: AppHintBoxProps) {
  const styles = VARIANT_STYLES[variant];

  return (
    <div
      role="note"
      className={`relative flex gap-2 rounded-lg border px-3 py-2.5 text-sm ${styles.container} ${className}`}
    >
      {!hideIcon && (
        <span className={`flex-shrink-0 mt-0.5 ${styles.icon}`}>{styles.iconNode}</span>
      )}
      <div className="flex-1 min-w-0">
        {title && <p className={`font-semibold mb-0.5 ${styles.title}`}>{title}</p>}
        <div className="leading-relaxed break-words">{children}</div>
      </div>
      {onDismiss && (
        <button
          type="button"
          aria-label="닫기"
          onClick={onDismiss}
          className={`flex-shrink-0 -mt-0.5 -mr-1 p-1 rounded hover:bg-black/5 transition-colors ${styles.icon}`}
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export default AppHintBox;
